import React, { useState, type ReactNode } from 'react';
import type { ApolloClient } from 'apollo-client';
import { type Extension } from '@atlassian/forge-ui/ui';
import { RendererNext, WebRuntime } from '@atlassian/forge-ui/ui';
import type {
	CoreData,
	ExtensionData,
	ExtensionViewData,
	ForgeDoc,
	ExtensionPayload,
} from '@atlassian/forge-ui-types';
import { useGetStaticMacroContent } from '../hooks/useGetStaticMacroContent';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { useShouldRenderRuntime } from '../hooks/useShouldRenderRuntime';
import type { CustomEditContext, EditorAPI } from '../../types';
import type { BridgeConfig } from '@atlassian/forge-ui';

interface StaticMacroRendererProps {
	accountId: string;
	apolloClient: ApolloClient<object>;
	bridge?: BridgeConfig;
	coreData: CoreData;
	editorAPI?: EditorAPI;
	extension: Extension;
	extensionData: ExtensionData;
	extensionPayload: ExtensionPayload;
	extensionViewData: ExtensionViewData;
	getContextToken?: () => Promise<string>;
	loadingComponent?: ReactNode;
	locale: string;
	modalExtension?: CustomEditContext['modalExtension'];
	onForgeDocUpdated?: (forgeDoc?: ForgeDoc) => void;
	onMacroConfigForgeDoc: (forgeDoc: ForgeDoc) => void;
	pageId: string;
}

const getCustomContentType = ({
	appId,
	envId,
	customContentTypeKey,
}: {
	appId: string;
	customContentTypeKey: string;
	envId: string;
}) => {
	return `forge:${appId}:${envId}:${customContentTypeKey}`;
};

export const StaticMacroRenderer = ({
	accountId,
	apolloClient,
	bridge,
	coreData,
	editorAPI,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	loadingComponent,
	locale,
	modalExtension,
	onForgeDocUpdated,
	onMacroConfigForgeDoc,
	pageId,
}: StaticMacroRendererProps) => {
	const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);

	const staticContentConfigKey = extension.properties?.staticView?.contentTitleConfigKey;
	const configValue = extensionPayload.config?.[staticContentConfigKey] || '';

	const { adfMapping, isLoading } = useGetStaticMacroContent({
		apolloClient: apolloClient,
		pageId: pageId,
		customContentType: getCustomContentType({
			appId: extension?.appId || '',
			envId: extension?.environmentId || '',
			customContentTypeKey: extension?.properties?.staticView?.customContentTypeKey || '',
		}),
	});

	const { shouldRenderRuntime } = useShouldRenderRuntime({
		editorAPI: editorAPI,
		extensionProperties: extension.properties,
		macroConfig: extensionPayload?.config || {},
		localId: coreData.localId,
	});

	const adfContent = adfMapping?.[configValue];

	const { cloudId } = coreData;

	return (
		<>
			{adfContent ? (
				<>
					{/* RendererActionsContext is required to render another renderer within the editor */}
					<RendererActionsContext>
						<ReactRenderer document={adfContent} />
					</RendererActionsContext>
				</>
			) : isLoading ? null : (
				// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				<div>Content missing for value: {configValue}</div>
			)}

			{/* Required to render the custom config modal */}
			{modalExtension ? (
				<RendererNext
					forgeDoc={forgeDoc}
					extension={extension}
					loading
					loadingComponent={loadingComponent}
					modalExtension={modalExtension}
					extensionType={extensionData.type}
					isNative
				/>
			) : null}

			{/* Render web runtime when config is opened */}
			{modalExtension || shouldRenderRuntime ? (
				<WebRuntime
					accountId={accountId}
					setForgeDoc={(forgeDoc: ForgeDoc) => {
						if (forgeDoc.type !== 'MacroConfig') {
							setForgeDoc(forgeDoc);
						}

						if (forgeDoc.type === 'MacroConfig') {
							onMacroConfigForgeDoc(forgeDoc);
						}

						onForgeDocUpdated?.(forgeDoc);
					}}
					apolloClient={apolloClient}
					contextIds={[`ari:cloud:confluence::site/${cloudId}`]}
					extension={extension}
					coreData={coreData}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					extensionViewData={extensionViewData}
					locale={locale}
					bridge={bridge}
					getContextToken={getContextToken}
				/>
			) : null}
		</>
	);
};
